// Shared UI primitives — same look, spacing and touch targets as the original dashboard.

import { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import { C } from './lib/format.js';

Chart.defaults.color = '#8CC8E8';
Chart.defaults.borderColor = '#1A3250';
Chart.defaults.font.family = "'DM Sans',system-ui,sans-serif";

// ── Responsive width hook ──
export function useWidth() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h, { passive: true });
    window.addEventListener('orientationchange', h, { passive: true });
    return () => {
      window.removeEventListener('resize', h);
      window.removeEventListener('orientationchange', h);
    };
  }, []);
  return w;
}

// ── Chart wrapper: rebuilds when `id` changes, always destroys cleanly ──
export function CChart({ cfg, height, id }) {
  const ref = useRef(null), inst = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    if (inst.current) { inst.current.destroy(); inst.current = null; }
    inst.current = new Chart(ref.current, cfg);
    return () => { if (inst.current) { inst.current.destroy(); inst.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return (
    <div style={{ position: 'relative', height: height || 280, width: '100%' }}>
      <canvas ref={ref} />
    </div>
  );
}

export function Card({ children, style }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 14,
      padding: '20px 18px', ...style,
    }}>{children}</div>
  );
}

export function H2({ children, sub }) {
  return (
    <div style={{ marginBottom: sub ? 4 : 16 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.bright, letterSpacing: '-.3px' }}>{children}</h2>
      {sub && <p style={{ margin: '4px 0 12px', fontSize: 12, color: C.dim, lineHeight: 1.5 }}>{sub}</p>}
    </div>
  );
}

// Minimum 44px touch target throughout.
export function Pill({ label, active, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      minHeight: 44, padding: '10px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
      fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 600,
      background: active ? (color || C.gold) : C.surf,
      color: active ? '#000' : C.text, transition: 'all .15s',
    }}>{label}</button>
  );
}

export function Btn({ children, onClick, tone, disabled, style }) {
  const col = tone === 'primary' ? C.gold : tone === 'danger' ? C.red : C.steel;
  const solid = tone === 'primary';
  return (
    <button onClick={onClick} disabled={disabled} style={{
      minHeight: 48, padding: '12px 20px', borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
      fontFamily: "'DM Sans',system-ui", fontSize: 15, fontWeight: 600,
      background: disabled ? C.surf : solid ? col : 'transparent',
      color: disabled ? C.dim : solid ? '#000' : col,
      border: solid ? 'none' : `1px solid ${disabled ? C.bdr : col}66`,
      opacity: disabled ? .55 : 1, transition: 'all .15s', ...style,
    }}>{children}</button>
  );
}

export function MCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: C.card, border: `1px solid ${C.bdr}`, borderRadius: 12,
      padding: '16px 16px', borderTop: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: C.lbl, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color, fontFamily: "'DM Mono',monospace" }}>{value}</div>
      <div style={{ fontSize: 11, color: C.dim, marginTop: 4, lineHeight: 1.4 }}>{sub}</div>
    </div>
  );
}

export function SGroup({ title, cols, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: C.gold, textTransform: 'uppercase', letterSpacing: 1.5,
        marginBottom: 12, paddingBottom: 7, borderBottom: `1px solid ${C.bdr}`,
      }}>{title}</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols || 2},1fr)`, gap: '12px 16px' }}>{children}</div>
    </div>
  );
}

// Number input. 16px font size is what stops iOS Safari zooming on focus.
export function SI({ label, sk, prefix, suffix, step, min, s, upd, disabled }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: C.lbl, marginBottom: 5 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {prefix && <span style={{ fontSize: 14, color: C.dim, flexShrink: 0 }}>{prefix}</span>}
        <input
          type="number" inputMode="decimal" value={s[sk]} disabled={disabled}
          onChange={e => upd(sk, parseFloat(e.target.value) || 0)}
          step={step || '1'} min={min || '0'}
        />
        {suffix && <span style={{ fontSize: 14, color: C.dim, flexShrink: 0 }}>{suffix}</span>}
      </div>
    </div>
  );
}

export function SCheck({ label, sk, s, upd, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, gridColumn: '1/-1', padding: '6px 0', minHeight: 44 }}>
      <input type="checkbox" checked={!!s[sk]} disabled={disabled}
        onChange={e => upd(sk, e.target.checked)} id={`ck_${sk}`} />
      <label htmlFor={`ck_${sk}`} style={{ fontSize: 14, color: C.text, cursor: 'pointer', lineHeight: 1.4 }}>{label}</label>
    </div>
  );
}

// Inline notice used for disclaimers, offline state and read-only banners.
export function Notice({ children, tone }) {
  const col = tone === 'warn' ? C.amb : tone === 'danger' ? C.red : tone === 'good' ? C.teal : C.steel;
  return (
    <div style={{
      background: col + '15', border: `1px solid ${col}44`, borderRadius: 12,
      padding: '14px 16px', fontSize: 13, color: C.dim, lineHeight: 1.6,
    }}>{children}</div>
  );
}
